import { useCallback, useEffect, useRef, useState } from 'react';

import {
  api,
  type ClientRow,
  type ContractorRow,
  type ExceptionItem,
  type OorwinSyncStatusResponse,
  type PayrollPreview,
  type PipelineStage,
  type PipelineStatus,
  type RecruiterRow,
  type Summary,
  type TrendPoint,
  type WorkforceComposition,
} from './api';
import { BarList, BarListSummary, RevenueArea } from './components/Charts';
import {
  Badge,
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import {
  KpiCard,
  MarginBadge,
  MiniDonut,
  SeverityBar,
  StackedBar,
  computeTrend,
} from './components/KpiCard';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { hours, marginPercent, money, moneyCompact, rate, shortDate } from './format';
import { Shell, type ShellProps } from './Shell';
import { StagePanel } from './stages/StagePanels';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { Modal } from './components/Modal';
import { ResolveExceptionForm } from './components/ResolveExceptionForm';

interface Data {
  readonly summary: Summary;
  readonly trend: readonly TrendPoint[];
  readonly clients: readonly ClientRow[];
  readonly contractors: readonly ContractorRow[];
  readonly recruiters: readonly RecruiterRow[];
  readonly pipeline: PipelineStatus;
  readonly exceptions: readonly ExceptionItem[];
  readonly workforce: WorkforceComposition;
}

type View = 'workflow' | 'payroll' | 'analysis';
type Tab = 'contractors' | 'clients' | 'recruiters';

export function Workspace(props: ShellProps) {
  const { session, navigate } = props;
  const [view, setView] = useState<View>('workflow');
  const [tab, setTab] = useState<Tab>('contractors');
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const tf = useTimeFilter();
  const [payrollData, setPayrollData] = useState<PayrollPreview | null>(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState<string | null>(null);

  const canSeeCommission = session.permissions.includes('commission:read:all');

  // Oorwin sync status — polled for progress bar
  const [syncStatus, setSyncStatus] = useState<OorwinSyncStatusResponse | null>(null);

  const reloadQueue = tf.reloadPeriodQueue;

  // Pipeline endpoints use periodId when a pay period is selected,
  // otherwise auto-detect the most urgent run.
  const activePeriodId = tf.toPeriodId();

  const {
    data,
    error,
    reload: reloadData,
  } = useAsyncData<Data>(
    () => {
      const dateRange = tf.toDateRange();
      return Promise.all([
        api.summary(dateRange),
        api.trend(dateRange),
        api.clients(dateRange),
        api.contractors(dateRange),
        canSeeCommission ? api.recruiters(dateRange) : Promise.resolve([]),
        api.pipeline(activePeriodId),
        api.exceptions(activePeriodId),
        api.workforceComposition(),
      ]).then(
        ([
          summary,
          trend,
          clients,
          contractors,
          recruiters,
          pipeline,
          exceptionsRes,
          workforce,
        ]) => ({
          summary,
          trend,
          clients,
          contractors,
          recruiters,
          pipeline,
          exceptions: exceptionsRes.exceptions,
          workforce,
        }),
      );
    },
    [canSeeCommission, tf.selection],
    'Could not load the workspace.',
  );

  // Poll sync status while a sync or history load is running
  const isRunning =
    syncStatus?.currentSync?.status === 'running' || syncStatus?.backfill?.status === 'running';
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await api.oorwinSyncStatus();
        if (!cancelled) {
          const wasRunning = isRunning;
          setSyncStatus(status);
          if (status.currentSync !== null || status.backfill?.status === 'running') {
            timer = setTimeout(poll, 2000);
          } else if (wasRunning) {
            reloadData();
          }
        }
      } catch {
        // Silently ignore
      }
    };

    if (isRunning) {
      timer = setTimeout(poll, 2000);
    }

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [isRunning, reloadData]);

  // Syncs start on the server when someone signs in; this page only reports them.
  useEffect(() => {
    let cancelled = false;
    api
      .oorwinSyncStatus()
      .then((s) => {
        if (!cancelled) setSyncStatus(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear stage/payroll state when the global time filter changes
  const selectionRef = useRef(tf.selection);
  useEffect(() => {
    if (selectionRef.current !== tf.selection) {
      selectionRef.current = tf.selection;
      setSelectedStage(null);
      setPayrollData(null);
      setPayrollError(null);
    }
  }, [tf.selection]);

  useEffect(() => {
    if (view !== 'payroll' || payrollData !== null) return;
    let cancelled = false;
    setPayrollLoading(true);

    api
      .payrollPreview(activePeriodId)
      .then((d) => {
        if (!cancelled) setPayrollData(d);
      })
      .catch((e) => {
        if (!cancelled) setPayrollError(e instanceof Error ? e.message : 'Could not load payroll.');
      })
      .finally(() => {
        if (!cancelled) setPayrollLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [view, payrollData, activePeriodId]);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading operations workspace…" />
        </Card>
      </Shell>
    );
  }

  const { summary, trend, clients, contractors, recruiters, pipeline, exceptions, workforce } =
    data;

  return (
    <Shell {...props}>
      <SyncStatusBar syncStatus={syncStatus} />

      <div className="mb-3 flex items-center justify-between gap-4">
        <div className="flex gap-1 rounded-lg border border-edge bg-surface-base p-0.5 w-fit">
          {(
            [
              ['workflow', 'Workflow'],
              ['payroll', 'Payroll'],
              ['analysis', 'Analysis'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                view === key ? 'bg-surface-overlay text-ink' : 'text-ink-faint hover:text-ink-muted'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <UnifiedTimeFilter />
        </div>
      </div>

      {view === 'workflow' && (
        <WorkflowView
          pipeline={pipeline}
          exceptions={exceptions}
          selectedStage={selectedStage}
          onSelectStage={setSelectedStage}
          navigate={navigate}
          canResolveExceptions={session.permissions.includes('time:write')}
          reloadData={() => {
            reloadData();
            reloadQueue();
          }}
          {...(activePeriodId !== undefined ? { periodId: activePeriodId } : {})}
        />
      )}

      {view === 'payroll' && (
        <PayrollView data={payrollData} loading={payrollLoading} error={payrollError} />
      )}

      {view === 'analysis' && (
        <AnalysisView
          summary={summary}
          trend={trend}
          workforce={workforce}
          exceptions={exceptions}
          clients={clients}
          contractors={contractors}
          recruiters={recruiters}
          canSeeCommission={canSeeCommission}
          tab={tab}
          setTab={setTab}
          search={search}
          setSearch={setSearch}
          navigate={navigate}
        />
      )}
    </Shell>
  );
}

function WorkflowView({
  pipeline,
  exceptions,
  selectedStage,
  onSelectStage,
  navigate,
  canResolveExceptions,
  reloadData,
  periodId,
}: {
  pipeline: PipelineStatus;
  exceptions: readonly ExceptionItem[];
  selectedStage: string | null;
  onSelectStage: (id: string | null) => void;
  navigate: (hash: string) => void;
  canResolveExceptions: boolean;
  reloadData: () => void;
  periodId?: string;
}) {
  const [exceptionsExpanded, setExceptionsExpanded] = useState(false);
  const activeStage = pipeline.stages.find((s) => s.id === selectedStage) ?? null;
  const onRefresh = reloadData;

  useEffect(() => {
    if (selectedStage !== null) return;
    const current =
      pipeline.stages.find((s) => s.status === 'in_progress') ??
      pipeline.stages.find((s) => s.status !== 'complete');
    if (current !== undefined) onSelectStage(current.id);
  }, [pipeline.stages, selectedStage, onSelectStage]);

  // ── Payroll KPI data ──
  const v = pipeline.verification;
  const hs = pipeline.hoursSummary;

  // Pay period deadline: submission is 2 business days before pay date
  const daysUntilPayDate = (() => {
    if (!pipeline.payDate) return null;
    const now = new Date();
    const pay = new Date(pipeline.payDate + 'T12:00:00Z');
    const diff = Math.ceil((pay.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  // Hours delta vs prior period
  const hoursDelta = (() => {
    if (!hs || hs.priorTotal === null) return null;
    const cur = Number(hs.total);
    const prior = Number(hs.priorTotal);
    if (prior === 0) return null;
    const pct = ((cur - prior) / prior) * 100;
    return { pct, direction: pct >= 0 ? ('up' as const) : ('down' as const) };
  })();

  // Deadline tone
  const deadlineTone: 'profit' | 'caution' | 'danger' | 'neutral' =
    daysUntilPayDate === null
      ? 'neutral'
      : daysUntilPayDate > 7
        ? 'profit'
        : daysUntilPayDate > 3
          ? 'caution'
          : 'danger';

  const severityCounts = { high: 0, medium: 0, low: 0 };
  for (const ex of exceptions) {
    severityCounts[ex.severity] += 1;
  }
  const hasHighSeverity = severityCounts.high > 0;

  // Period date formatting
  const fmtShort = (iso: string | undefined) => {
    if (!iso) return '—';
    const d = new Date(iso + 'T12:00:00Z');
    return `${String(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}`;
  };

  return (
    <>
      {/* Compact KPI strip */}
      <div className="grid grid-cols-4 gap-3 mb-3">
        <MetricCell
          label="Pay Period"
          value={daysUntilPayDate !== null ? `${String(daysUntilPayDate)}d` : '—'}
          subtitle={`${fmtShort(pipeline.periodStart)} – ${fmtShort(pipeline.periodEnd)}, pays ${fmtShort(pipeline.payDate)}`}
          tone={deadlineTone}
          footer={
            daysUntilPayDate !== null ? <DeadlineBar daysLeft={daysUntilPayDate} /> : undefined
          }
        />
        <MetricCell
          label="Verified"
          value={v ? `${String(v.verified)} / ${String(v.total)}` : '—'}
          subtitle={
            v ? `${String(Math.round((v.verified / Math.max(v.total, 1)) * 100))}% complete` : ''
          }
          tone={
            v && v.total > 0 && v.verified === v.total
              ? 'profit'
              : v && v.flagged > 0
                ? 'caution'
                : 'neutral'
          }
          footer={
            v && v.total > 0 ? (
              <VerifyBar
                verified={v.verified}
                flagged={v.flagged}
                rejected={v.rejected}
                total={v.total}
              />
            ) : undefined
          }
        />
        <MetricCell
          label="Period Hours"
          value={hs ? hours(hs.total) : '—'}
          tone={hoursDelta && Math.abs(hoursDelta.pct) > 15 ? 'caution' : 'neutral'}
          trend={
            hoursDelta
              ? {
                  pct: `${Math.abs(hoursDelta.pct).toFixed(1)}%`,
                  direction: hoursDelta.direction,
                  favorable: hoursDelta.direction === 'up',
                }
              : undefined
          }
          trendContext="vs prior period"
          footer={
            hs ? (
              <HoursBar
                regular={Number(hs.regular)}
                overtime={Number(hs.overtime)}
                holiday={Number(hs.holiday)}
                vacation={Number(hs.vacation)}
              />
            ) : undefined
          }
        />
        <button
          type="button"
          onClick={() => {
            if (exceptions.length > 0) setExceptionsExpanded((prev) => !prev);
          }}
          className="text-left"
        >
          <MetricCell
            label="Alerts"
            value={String(exceptions.length)}
            subtitle={exceptions.length > 0 ? 'requires attention' : 'all clear'}
            tone={hasHighSeverity ? 'danger' : exceptions.length > 0 ? 'caution' : 'neutral'}
            footer={exceptions.length > 0 ? <SeverityStrip counts={severityCounts} /> : undefined}
          />
        </button>
      </div>

      {exceptionsExpanded && exceptions.length > 0 && (
        <Card className="mt-2 rise" delay={0}>
          <CardHeader
            title="Alerts requiring attention"
            aside={
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-caution/10 px-2.5 py-0.5 text-xs font-medium text-caution">
                  {exceptions.length}
                </span>
                <button
                  type="button"
                  onClick={() => setExceptionsExpanded(false)}
                  className="rounded-lg p-1 text-ink-faint hover:text-ink-muted hover:bg-surface-overlay transition-colors"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              </div>
            }
          />
          <ExceptionsQueue
            exceptions={exceptions}
            navigate={navigate}
            canResolve={canResolveExceptions}
            onResolved={reloadData}
          />
        </Card>
      )}

      <CompactStepper
        stages={pipeline.stages}
        selectedId={selectedStage}
        onSelect={onSelectStage}
      />

      {activeStage !== null && (
        <>
          <StagePanel
            stageId={activeStage.id}
            stageStatus={activeStage.status}
            nextStageName={(() => {
              const idx = pipeline.stages.findIndex((s) => s.id === activeStage.id);
              const next = idx >= 0 ? pipeline.stages[idx + 1] : undefined;
              return next?.label;
            })()}
            onRefresh={onRefresh}
            onAdvance={() => {
              const idx = pipeline.stages.findIndex((s) => s.id === activeStage.id);
              const next = idx >= 0 ? pipeline.stages[idx + 1] : undefined;
              if (next !== undefined) onSelectStage(next.id);
            }}
            {...(periodId !== undefined ? { periodId } : {})}
          />
        </>
      )}
    </>
  );
}

const metricBorder: Record<string, string> = {
  profit: 'border-l-profit',
  revenue: 'border-l-revenue',
  caution: 'border-l-caution',
  danger: 'border-l-danger',
  neutral: 'border-l-ink-faint',
};
const metricValue: Record<string, string> = {
  profit: 'text-profit',
  revenue: 'text-revenue',
  caution: 'text-caution',
  danger: 'text-danger',
  neutral: 'text-ink',
};

function MetricCell({
  label,
  value,
  subtitle,
  tone = 'neutral',
  trend,
  trendContext,
  footer,
}: {
  label: string;
  value: string;
  subtitle?: string | undefined;
  tone?: 'profit' | 'caution' | 'danger' | 'neutral' | undefined;
  trend?: { pct: string; direction: 'up' | 'down' | 'flat'; favorable: boolean } | undefined;
  trendContext?: string | undefined;
  footer?: React.ReactNode | undefined;
}) {
  return (
    <section
      className={`flex flex-col justify-between rounded-xl border border-edge border-l-4 bg-surface-raised p-3 shadow-sm ${metricBorder[tone]}`}
    >
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
          {label}
        </p>
        <p className={`text-xl font-bold leading-tight mt-0.5 ${metricValue[tone]}`}>{value}</p>
        <div className="mt-0.5 flex flex-col gap-0.5">
          {subtitle && <p className="text-[10px] text-ink-faint">{subtitle}</p>}
          {trend && trend.direction !== 'flat' && (
            <span className="inline-flex items-center gap-1">
              <span
                className={`text-xs font-bold ${trend.favorable ? 'text-profit' : 'text-danger'}`}
              >
                {trend.direction === 'up' ? '↑' : '↓'} {trend.pct}
              </span>
              {trendContext && <span className="text-[9px] text-ink-faint">{trendContext}</span>}
            </span>
          )}
        </div>
      </div>
      {footer && <div className="mt-2 w-full">{footer}</div>}
    </section>
  );
}

/** Full-width progress bar for deadline countdown. */
function DeadlineBar({ daysLeft, totalDays = 28 }: { daysLeft: number; totalDays?: number }) {
  const elapsed = Math.max(0, Math.min(100, ((totalDays - daysLeft) / totalDays) * 100));
  const color = daysLeft <= 3 ? '#ef4444' : daysLeft <= 7 ? '#f59e0b' : '#22c55e';
  return (
    <div className="w-full">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div
          className="rounded-full transition-all"
          style={{ width: `${String(elapsed)}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-ink-faint">period start</span>
        <span className="text-[9px] text-ink-faint">pay date</span>
      </div>
    </div>
  );
}

/** Full-width segmented bar for verification status. */
function VerifyBar({
  verified,
  flagged,
  rejected,
  total,
}: {
  verified: number;
  flagged: number;
  rejected: number;
  total: number;
}) {
  if (total === 0) return <div className="h-2 w-full rounded-full bg-surface-overlay" />;
  const pending = Math.max(0, total - verified - flagged - rejected);
  const segments = [
    { value: verified, color: '#22c55e', label: 'Verified' },
    { value: flagged, color: '#f59e0b', label: 'Flagged' },
    { value: rejected, color: '#ef4444', label: 'Rejected' },
    { value: pending, color: 'transparent', label: 'Pending' },
  ];
  return (
    <div className="w-full">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        {segments
          .filter((s) => s.value > 0 && s.color !== 'transparent')
          .map((seg, i) => (
            <div
              key={i}
              className="transition-all"
              style={{ width: `${String((seg.value / total) * 100)}%`, backgroundColor: seg.color }}
            />
          ))}
      </div>
      <div className="flex gap-3 mt-1">
        {segments
          .filter((s) => s.value > 0)
          .map((seg, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[9px] text-ink-faint">
              {seg.color !== 'transparent' && (
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: seg.color }} />
              )}
              {seg.value} {seg.label.toLowerCase()}
            </span>
          ))}
      </div>
    </div>
  );
}

/** Full-width stacked bar for hour categories with legend. */
function HoursBar({
  regular,
  overtime,
  holiday,
  vacation,
}: {
  regular: number;
  overtime: number;
  holiday: number;
  vacation: number;
}) {
  const segments = [
    { value: regular, color: '#3b82f6', label: 'Regular' },
    { value: overtime, color: '#f59e0b', label: 'OT' },
    { value: holiday, color: '#22c55e', label: 'Holiday' },
    { value: vacation, color: '#8b5cf6', label: 'Vacation' },
  ];
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  return (
    <div className="w-full">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        {total > 0 &&
          segments
            .filter((s) => s.value > 0)
            .map((seg, i) => (
              <div
                key={i}
                className="transition-all"
                style={{
                  width: `${String((seg.value / total) * 100)}%`,
                  backgroundColor: seg.color,
                }}
              />
            ))}
      </div>
      <div className="flex gap-3 mt-1">
        {segments.map((seg, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[9px] text-ink-faint">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: seg.color }} />
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Full-width severity bar for alerts. */
function SeverityStrip({ counts }: { counts: { high: number; medium: number; low: number } }) {
  const segments = [
    { value: counts.high, color: '#ef4444', label: 'High' },
    { value: counts.medium, color: '#f59e0b', label: 'Medium' },
    { value: counts.low, color: '#94a3b8', label: 'Low' },
  ];
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  return (
    <div className="w-full">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        {segments
          .filter((s) => s.value > 0)
          .map((seg, i) => (
            <div
              key={i}
              className="transition-all"
              style={{ width: `${String((seg.value / total) * 100)}%`, backgroundColor: seg.color }}
            />
          ))}
      </div>
      <div className="flex gap-3 mt-1">
        {segments
          .filter((s) => s.value > 0)
          .map((seg, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[9px] text-ink-faint">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: seg.color }} />
              {seg.value} {seg.label.toLowerCase()}
            </span>
          ))}
      </div>
    </div>
  );
}

function CompactStepper({
  stages,
  selectedId,
  onSelect,
}: {
  stages: readonly PipelineStage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <nav className="flex items-center gap-1 py-3 overflow-x-auto" aria-label="Pipeline progress">
      {stages.map((stage, index) => {
        const isSelected = stage.id === selectedId;
        const allPriorComplete =
          index === 0 || stages.slice(0, index).every((s) => s.status === 'complete');
        const reachable =
          stage.status === 'complete' || stage.status === 'in_progress' || allPriorComplete;

        return (
          <div key={stage.id} className="flex items-center">
            {index > 0 && (
              <svg
                className="h-3 w-3 mx-1 text-ink-faint shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
            <button
              type="button"
              disabled={!reachable}
              onClick={() => {
                if (reachable) onSelect(stage.id);
              }}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs transition-colors ${
                !reachable
                  ? 'text-ink-faint/50 cursor-not-allowed'
                  : isSelected
                    ? 'bg-surface-overlay font-semibold text-ink ring-1 ring-edge'
                    : 'text-ink-muted hover:bg-surface-overlay/50 hover:text-ink'
              }`}
            >
              {stage.status === 'complete' && (
                <svg
                  className="h-3.5 w-3.5 text-profit shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {stage.status === 'in_progress' && (
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-caution opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-caution" />
                </span>
              )}
              {stage.status === 'pending' && (
                <span className="h-2 w-2 rounded-full bg-ink-faint/40 shrink-0" />
              )}
              {stage.label}
            </button>
          </div>
        );
      })}
    </nav>
  );
}

function ExceptionsQueue({
  exceptions,
  navigate,
  canResolve,
  onResolved,
}: {
  exceptions: readonly ExceptionItem[];
  navigate: (hash: string) => void;
  canResolve: boolean;
  onResolved: () => void;
}) {
  const [selectedItem, setSelectedItem] = useState<ExceptionItem | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());

  const visible = exceptions.filter((e) => !dismissed.has(e.id));

  if (visible.length === 0) {
    return <p className="px-4 pb-4 text-sm text-ink-muted">No exceptions for this period.</p>;
  }

  // Recorded exceptions are grouped together: each needs a decision, and each
  // row's title already names the kind of problem.
  const grouped = new Map<string, ExceptionItem[]>();
  for (const item of visible) {
    const key = item.resolvable === true ? 'recorded' : item.type;
    const group = grouped.get(key) ?? [];
    group.push(item);
    grouped.set(key, group);
  }

  const typeLabels: Record<string, string> = {
    recorded: 'Needs a Decision',
    missing_timesheet: 'Missing Timesheets',
    hours_variance: 'Hours Variance',
    pto_warning: 'PTO Warnings',
    rate_change: 'Rate Changes',
  };

  return (
    <div className="px-4 pb-4 space-y-4">
      {Array.from(grouped.entries()).map(([type, items]) => (
        <div key={type}>
          <h4 className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
            {typeLabels[type] ?? type}
          </h4>
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors cursor-pointer hover:bg-surface-overlay ${
                  reviewed.has(item.id) ? 'border-profit/30 bg-profit/5' : 'border-edge'
                }`}
                onClick={() => setSelectedItem(item)}
              >
                <SeverityDot severity={item.severity} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">{item.title}</p>
                  <p className="text-xs text-ink-muted mt-0.5">{item.detail}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {reviewed.has(item.id) && (
                    <span className="text-[10px] font-medium text-profit">Reviewed</span>
                  )}
                  {item.resolvable !== true && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDismissed((prev) => new Set(prev).add(item.id));
                      }}
                      className="rounded p-1 text-ink-faint hover:text-ink-muted hover:bg-surface-overlay transition-colors"
                      title="Dismiss"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal
        open={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
        title={selectedItem?.title ?? ''}
        subtitle={selectedItem?.detail}
      >
        {selectedItem !== null && (
          <ExceptionDetailContent
            item={selectedItem}
            isReviewed={reviewed.has(selectedItem.id)}
            canResolve={canResolve}
            onReview={() => {
              setReviewed((prev) => new Set(prev).add(selectedItem.id));
              setSelectedItem(null);
            }}
            onDismiss={() => {
              setDismissed((prev) => new Set(prev).add(selectedItem.id));
              setSelectedItem(null);
            }}
            onResolved={() => {
              setDismissed((prev) => new Set(prev).add(selectedItem.id));
              setSelectedItem(null);
              onResolved();
            }}
            onNavigate={(hash) => {
              setSelectedItem(null);
              navigate(hash);
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function ExceptionDetailContent({
  item,
  isReviewed,
  canResolve,
  onReview,
  onDismiss,
  onResolved,
  onNavigate,
}: {
  item: ExceptionItem;
  isReviewed: boolean;
  canResolve: boolean;
  onReview: () => void;
  onDismiss: () => void;
  onResolved: () => void;
  onNavigate: (hash: string) => void;
}) {
  if (item.resolvable === true) {
    return (
      <div className="space-y-5">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <SeverityDot severity={item.severity} />
            <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">
              {item.severity} severity
            </span>
          </div>
          <p className="text-sm text-ink">{item.detail}</p>
        </section>
        <section className="border-t border-edge pt-4">
          {canResolve ? (
            <ResolveExceptionForm exceptionId={item.id} onResolved={onResolved} />
          ) : (
            <p className="text-sm text-ink-muted">
              This stays open until someone who can amend time resolves it with a note.
            </p>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center gap-2 mb-3">
          <SeverityDot severity={item.severity} />
          <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">
            {item.severity} severity
          </span>
        </div>
        <p className="text-sm text-ink">{item.detail}</p>
      </section>

      {/* Contact info for missing timesheets */}
      {item.type === 'missing_timesheet' &&
        (item.email !== undefined || item.phone !== undefined) && (
          <section>
            <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
              Contact
            </h3>
            <div className="space-y-1.5 text-sm">
              {item.clientName !== undefined && (
                <p className="text-ink-muted">Client: {item.clientName}</p>
              )}
              {item.email != null && (
                <p>
                  <a href={`mailto:${item.email}`} className="text-revenue hover:underline">
                    {item.email}
                  </a>
                </p>
              )}
              {item.phone != null && (
                <p>
                  <a href={`tel:${item.phone}`} className="text-revenue hover:underline">
                    {item.phone}
                  </a>
                </p>
              )}
            </div>
          </section>
        )}

      {item.type === 'hours_variance' && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            Suggested Action
          </h3>
          <p className="text-sm text-ink-muted mb-2">
            Review this contractor's hours in the Verify step to see the full breakdown and take
            action.
          </p>
        </section>
      )}

      {item.type === 'rate_change' && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            Rate Change Info
          </h3>
          <p className="text-sm text-ink-muted">
            Confirm the new rate is reflected correctly in this period's GP calculation.
          </p>
        </section>
      )}

      {item.type === 'pto_warning' && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            PTO Policy
          </h3>
          <p className="text-sm text-ink-muted">
            Negative PTO balances are allowed up to -24 hours while accruing. Check if this
            contractor's usage exceeds the threshold.
          </p>
        </section>
      )}

      <section className="border-t border-edge pt-4 flex flex-wrap items-center gap-2">
        {!isReviewed ? (
          <button
            type="button"
            onClick={onReview}
            className="rounded-lg border border-profit bg-profit/10 px-4 py-2 text-sm font-medium text-profit hover:bg-profit/20 transition-colors"
          >
            Mark as Reviewed
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
            Reviewed
          </span>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
        >
          Dismiss
        </button>
        {item.entityId !== undefined && item.entityType === 'contractor' && (
          <button
            type="button"
            onClick={() => onNavigate(`contractor/${item.entityId}`)}
            className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
          >
            View Contractor
          </button>
        )}
        {item.type === 'missing_timesheet' && item.email != null && (
          <a
            href={`mailto:${item.email}`}
            className="rounded-lg border border-revenue/30 bg-revenue/10 px-4 py-2 text-sm font-medium text-revenue hover:bg-revenue/20 transition-colors"
          >
            Send Reminder
          </a>
        )}
      </section>
    </div>
  );
}

function SeverityDot({ severity }: { severity: ExceptionItem['severity'] }) {
  const color =
    severity === 'high' ? 'bg-caution' : severity === 'medium' ? 'bg-amber-400' : 'bg-ink-faint';

  return <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${color}`} />;
}

function PayrollView({
  data,
  loading,
  error,
}: {
  data: PayrollPreview | null;
  loading: boolean;
  error: string | null;
}) {
  const [search, setSearch] = useState('');

  if (error !== null) {
    return (
      <Card className="px-5 py-8">
        <p className="text-sm text-caution">{error}</p>
      </Card>
    );
  }

  if (loading || data === null) {
    return (
      <Card>
        <Spinner label="Loading payroll preview…" />
      </Card>
    );
  }

  const filteredLines = data.lines.filter(
    (l) =>
      search === '' ||
      l.workerName.toLowerCase().includes(search.toLowerCase()) ||
      l.clientName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card delay={0} className="px-5 py-4 lg:col-span-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Pay period
          </p>
          <p className="figure mt-2 text-lg font-semibold text-ink">
            Week ending {shortDate(data.period.weekEnding)}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-ink-muted">
              {shortDate(data.period.periodStart)} – {shortDate(data.period.periodEnd)}
            </span>
            <Badge tone={data.period.status === 'closed' ? 'real' : 'warning'}>
              {data.period.status}
            </Badge>
          </div>
        </Card>
        <Kpi label="Total hours" value={hours(data.totals.totalHours)} delay={50} />
        <Kpi label="Billable hours" value={hours(data.totals.billableHours)} delay={100} />
        <Kpi
          label="Estimated gross pay"
          value={moneyCompact(data.totals.totalPay)}
          exact={money(data.totals.totalPay)}
          tone="revenue"
          delay={150}
        />
      </div>

      <Card className="mt-4" delay={200}>
        <CardHeader
          title="Payroll staging preview"
          subtitle={`${String(filteredLines.length)} contractors`}
          aside={
            <div className="flex items-center gap-3">
              <div className="w-56">
                <SearchBar value={search} onChange={setSearch} placeholder="Filter rows…" />
              </div>
              <ExportButton
                data={filteredLines.map((l) => ({
                  contractor: l.workerName,
                  customer: l.clientName,
                  type: l.workerType === 'contractor_1099' ? '1099' : 'W2',
                  totalHours: hours(l.totalHours),
                  payRate: rate(l.payRate),
                  grossPay: money(l.totalPay),
                }))}
                filename="payroll-preview"
                headers={[
                  { key: 'contractor', label: 'Contractor' },
                  { key: 'customer', label: 'Customer' },
                  { key: 'type', label: 'Type' },
                  { key: 'totalHours', label: 'Total Hours' },
                  { key: 'payRate', label: 'Pay Rate' },
                  { key: 'grossPay', label: 'Gross Pay' },
                ]}
              />
            </div>
          }
        />
        <Table
          head={[
            { label: 'Contractor' },
            { label: 'Customer' },
            { label: 'Type' },
            { label: 'Total Hours', align: 'right' },
            { label: 'Pay Rate', align: 'right' },
            { label: 'Gross Pay', align: 'right' },
          ]}
        >
          {filteredLines.map((l) => (
            <tr
              key={l.workerId}
              className="border-b border-edge/50 transition-colors last:border-0 hover:bg-surface-overlay/60"
            >
              <Cell>{l.workerName}</Cell>
              <Cell>{l.clientName}</Cell>
              <Cell muted>{l.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
              <Cell align="right" mono>
                {hours(l.totalHours)}
              </Cell>
              <Cell align="right" mono>
                {rate(l.payRate)}
              </Cell>
              <Cell align="right" mono>
                <span className="text-revenue">{money(l.totalPay)}</span>
              </Cell>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

function AnalysisView({
  summary,
  trend,
  workforce,
  exceptions,
  clients,
  contractors,
  recruiters,
  canSeeCommission,
  tab,
  setTab,
  search,
  setSearch,
  navigate,
}: {
  summary: Summary;
  trend: readonly TrendPoint[];
  workforce: WorkforceComposition;
  exceptions: readonly ExceptionItem[];
  clients: readonly ClientRow[];
  contractors: readonly ContractorRow[];
  recruiters: readonly RecruiterRow[];
  canSeeCommission: boolean;
  tab: Tab;
  setTab: (t: Tab) => void;
  search: string;
  setSearch: (s: string) => void;
  navigate: (hash: string) => void;
}) {
  const SPARKLINE_WEEKS = 12;
  const recentTrend = trend.slice(-SPARKLINE_WEEKS);
  const priorTrend = trend.slice(-SPARKLINE_WEEKS * 2, -SPARKLINE_WEEKS);

  const billedSparkline = recentTrend.map((p) => Number(p.billed));
  const gpSparkline = recentTrend.map((p) => Number(p.grossProfit));
  const headcountSparkline = recentTrend.map((p) => p.contractors);

  const currentBilled = recentTrend.reduce((s, p) => s + Number(p.billed), 0);
  const priorBilled = priorTrend.reduce((s, p) => s + Number(p.billed), 0);
  const currentGp = recentTrend.reduce((s, p) => s + Number(p.grossProfit), 0);
  const priorGp = priorTrend.reduce((s, p) => s + Number(p.grossProfit), 0);
  const currentHead = recentTrend.length > 0 ? recentTrend[recentTrend.length - 1]!.contractors : 0;
  const priorHead = priorTrend.length > 0 ? priorTrend[priorTrend.length - 1]!.contractors : 0;

  const margin =
    Number(summary.billed) > 0 ? (Number(summary.grossProfit) / Number(summary.billed)) * 100 : 0;

  const severityCounts = { high: 0, medium: 0, low: 0 };
  for (const ex of exceptions) {
    severityCounts[ex.severity] += 1;
  }
  const hasHighSeverity = severityCounts.high > 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Billed Revenue"
          value={moneyCompact(summary.billed)}
          exact={money(summary.billed)}
          subtitle={`${summary.weeks.toLocaleString()} contractor-weeks`}
          tone="revenue"
          delay={0}
          trend={computeTrend(currentBilled, priorBilled)}
          trendContext={`vs prior ${String(SPARKLINE_WEEKS)}w`}
          sparkline={billedSparkline}
          sparklineColor="#2563eb"
          sparklineLabel="rev/wk"
        />
        <KpiCard
          label="Gross Profit"
          value={moneyCompact(summary.grossProfit)}
          exact={money(summary.grossProfit)}
          tone="profit"
          delay={50}
          trend={computeTrend(currentGp, priorGp)}
          trendContext={`vs prior ${String(SPARKLINE_WEEKS)}w`}
          sparkline={gpSparkline}
          sparklineColor="#16a34a"
          sparklineLabel="GP/wk"
        >
          <MarginBadge margin={margin} />
        </KpiCard>
        <KpiCard
          label="Active Placements"
          value={String(summary.activeHeadcount)}
          subtitle={`${String(workforce.w2)} W2 / ${String(workforce.contractor1099)} 1099`}
          tone="profit"
          delay={100}
          trend={computeTrend(currentHead, priorHead)}
          trendContext={`vs prior ${String(SPARKLINE_WEEKS)}w`}
          sparkline={headcountSparkline}
          sparklineColor="#16a34a"
          sparklineLabel="headcount"
        >
          <StackedBar
            segments={[
              { value: workforce.w2, color: '#16a34a', label: 'W2' },
              { value: workforce.contractor1099, color: '#2563eb', label: '1099' },
            ]}
          />
        </KpiCard>
        <KpiCard
          label="Exceptions"
          value={String(exceptions.length)}
          subtitle={exceptions.length > 0 ? 'requires attention' : 'all clear'}
          tone={hasHighSeverity ? 'danger' : exceptions.length > 0 ? 'caution' : 'neutral'}
          delay={150}
        >
          {exceptions.length > 0 && <SeverityBar counts={severityCounts} />}
        </KpiCard>
      </div>

      <Card className="mt-4" delay={200}>
        <CardHeader
          title="Billed revenue and gross profit"
          subtitle="By week ending. The gap between the lines is loaded cost."
        />
        <RevenueArea
          points={trend.map((point) => ({
            label: point.weekEnding,
            billed: point.billed,
            grossProfit: point.grossProfit,
          }))}
        />
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card delay={250}>
          <CardHeader title="Gross profit by customer" />
          {(() => {
            const top5 = clients.filter((c) => c.grossProfit !== '0').slice(0, 5);
            const top5Gp = top5.reduce((s, c) => s + Number(c.grossProfit), 0);
            const totalGp = Number(summary.grossProfit);
            const concentration = totalGp > 0 ? ((top5Gp / totalGp) * 100).toFixed(1) : '—';
            return (
              <BarListSummary
                viz={
                  <MiniDonut
                    size={36}
                    segments={[
                      { value: top5Gp, color: '#16a34a', label: 'Top 5' },
                      { value: Math.max(totalGp - top5Gp, 0), color: '#d4d4d4', label: 'Other' },
                    ]}
                  />
                }
              >
                <span className="figure font-semibold">{moneyCompact(summary.grossProfit)}</span>
                <span>
                  {clients.length} customer{clients.length !== 1 ? 's' : ''}
                </span>
                <span>Top 5 = {concentration}%</span>
              </BarListSummary>
            );
          })()}
          <BarList
            data={clients.map((client) => {
              const m =
                Number(client.billed) > 0
                  ? (Number(client.grossProfit) / Number(client.billed)) * 100
                  : 0;
              const concentration =
                Number(summary.grossProfit) > 0
                  ? (Number(client.grossProfit) / Number(summary.grossProfit)) * 100
                  : 0;
              return {
                label: client.name,
                value: client.grossProfit,
                note: `${client.contractors} contractors · ${hours(client.hours)} h · ${marginPercent(client.grossProfit, client.billed)} margin`,
                badge: {
                  text: `${m.toFixed(1)}%`,
                  tone:
                    m >= 25
                      ? ('profit' as const)
                      : m >= 10
                        ? ('caution' as const)
                        : ('danger' as const),
                },
                flagged: concentration > 15,
              };
            })}
          />
        </Card>

        <Card className="lg:col-span-2" delay={300}>
          <CardHeader
            title="Top contractors by gross profit"
            subtitle="Weighted gross profit per hour, across all weeks"
          />
          {(() => {
            const shown = contractors.slice(0, 8);
            const shownGp = shown.reduce((s, c) => s + Number(c.grossProfit), 0);
            const shownHours = shown.reduce((s, c) => s + Number(c.hours), 0);
            const avgGph = shownHours > 0 ? (shownGp / shownHours).toFixed(2) : '0';
            const w2Count = shown.filter((c) => c.workerType !== 'contractor_1099').length;
            const c1099Count = shown.filter((c) => c.workerType === 'contractor_1099').length;
            return (
              <BarListSummary
                vizGrow
                viz={
                  <StackedBar
                    segments={[
                      { value: w2Count, color: '#16a34a', label: 'W2' },
                      { value: c1099Count, color: '#2563eb', label: '1099' },
                    ]}
                  />
                }
              >
                <span>
                  <span className="figure font-semibold">{moneyCompact(shownGp.toFixed(4))}</span>{' '}
                  top-{shown.length} GP
                </span>
                <span>
                  <span className="figure font-semibold">${avgGph}</span>/h avg
                </span>
                <span>
                  {shown.length} contractor{shown.length !== 1 ? 's' : ''}
                </span>
              </BarListSummary>
            );
          })()}
          <BarList
            data={contractors.slice(0, 8).map((row) => ({
              label: `${row.name} — ${row.clientName}`,
              value: row.grossProfit,
              note: `${hours(row.hours)} h · ${rate(row.grossProfitPerHour)}/h · ${row.weeks} weeks`,
            }))}
            limit={8}
          />
        </Card>
      </div>

      <Card className="mt-4" delay={350}>
        <CardHeader
          title="Detail"
          aside={
            <div className="flex items-center gap-3">
              <div className="w-56">
                <SearchBar value={search} onChange={setSearch} placeholder="Filter rows…" />
              </div>
              <ExportButton
                data={
                  tab === 'contractors'
                    ? contractors
                        .filter(
                          (r) =>
                            search === '' ||
                            r.name.toLowerCase().includes(search.toLowerCase()) ||
                            r.clientName.toLowerCase().includes(search.toLowerCase()),
                        )
                        .map((r) => ({
                          name: r.name,
                          client: r.clientName,
                          type: r.workerType === 'contractor_1099' ? '1099' : 'W2',
                          weeks: r.weeks,
                          hours: hours(r.hours),
                          billRate: rate(r.billRate),
                          gpPerHour: rate(r.grossProfitPerHour),
                          grossProfit: money(r.grossProfit),
                        }))
                    : tab === 'clients'
                      ? clients
                          .filter(
                            (r) =>
                              search === '' || r.name.toLowerCase().includes(search.toLowerCase()),
                          )
                          .map((r) => ({
                            name: r.name,
                            contractors: r.contractors,
                            hours: hours(r.hours),
                            billed: money(r.billed),
                            grossProfit: money(r.grossProfit),
                            margin: marginPercent(r.grossProfit, r.billed),
                          }))
                      : recruiters
                          .filter(
                            (r) =>
                              search === '' || r.name.toLowerCase().includes(search.toLowerCase()),
                          )
                          .map((r) => ({
                            name: r.name,
                            contractors: r.contractors,
                            hours: hours(r.hours),
                            grossProfit: money(r.grossProfit),
                          }))
                }
                filename={`${tab}`}
                headers={
                  tab === 'contractors'
                    ? [
                        { key: 'name', label: 'Contractor' },
                        { key: 'client', label: 'Customer' },
                        { key: 'type', label: 'Type' },
                        { key: 'weeks', label: 'Weeks' },
                        { key: 'hours', label: 'Hours' },
                        { key: 'billRate', label: 'Bill Rate' },
                        { key: 'gpPerHour', label: 'GP/h' },
                        { key: 'grossProfit', label: 'Gross Profit' },
                      ]
                    : tab === 'clients'
                      ? [
                          { key: 'name', label: 'Customer' },
                          { key: 'contractors', label: 'Contractors' },
                          { key: 'hours', label: 'Hours' },
                          { key: 'billed', label: 'Billed' },
                          { key: 'grossProfit', label: 'Gross Profit' },
                          { key: 'margin', label: 'Margin' },
                        ]
                      : [
                          { key: 'name', label: 'Recruiter' },
                          { key: 'contractors', label: 'Contractors' },
                          { key: 'hours', label: 'Hours' },
                          { key: 'grossProfit', label: 'Gross Profit' },
                        ]
                }
              />
              <div className="flex gap-1 rounded-lg border border-edge bg-surface-base p-0.5">
                {(
                  [
                    ['contractors', `Contractors (${contractors.length})`],
                    ['clients', `Customers (${clients.length})`],
                    ...(canSeeCommission
                      ? ([['recruiters', `Recruiters (${recruiters.length})`]] as const)
                      : []),
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                      tab === key
                        ? 'bg-surface-overlay text-ink'
                        : 'text-ink-faint hover:text-ink-muted'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          }
        />

        {tab === 'contractors' && (
          <ContractorTable
            rows={contractors.filter(
              (r) =>
                search === '' ||
                r.name.toLowerCase().includes(search.toLowerCase()) ||
                r.clientName.toLowerCase().includes(search.toLowerCase()),
            )}
            navigate={navigate}
          />
        )}
        {tab === 'clients' && (
          <ClientTable
            rows={clients.filter(
              (r) => search === '' || r.name.toLowerCase().includes(search.toLowerCase()),
            )}
            navigate={navigate}
          />
        )}
        {tab === 'recruiters' && (
          <RecruiterTable
            rows={recruiters.filter(
              (r) => search === '' || r.name.toLowerCase().includes(search.toLowerCase()),
            )}
            navigate={navigate}
          />
        )}
      </Card>
    </>
  );
}

function ContractorTable({
  rows,
  navigate,
}: {
  rows: readonly ContractorRow[];
  navigate: (hash: string) => void;
}) {
  const showsRates = rows.some((row) => row.payRate !== null);

  const accessor = useCallback((row: ContractorRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      client: row.clientName,
      type: row.workerType,
      weeks: row.weeks,
      hours: row.hours,
      payRate: row.payRate,
      loadFactor: row.loadFactor,
      billRate: row.billRate,
      gpPerHour: row.grossProfitPerHour,
      grossProfit: row.grossProfit,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(rows, accessor);

  return (
    <Table
      head={[
        { label: 'Contractor', sortKey: 'name' },
        { label: 'Customer', sortKey: 'client' },
        { label: 'Type', sortKey: 'type' },
        { label: 'Weeks', align: 'right', sortKey: 'weeks' },
        { label: 'Hours', align: 'right', sortKey: 'hours' },
        ...(showsRates
          ? ([
              { label: 'Pay', align: 'right' as const, sortKey: 'payRate' },
              { label: 'Load', align: 'right' as const, sortKey: 'loadFactor' },
            ] as const)
          : []),
        { label: 'Bill', align: 'right', sortKey: 'billRate' },
        { label: 'GP/h', align: 'right', sortKey: 'gpPerHour' },
        { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
      ]}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={onSort}
    >
      {sorted.map((row) => (
        <Row key={row.id} onClick={() => navigate(`contractor/${row.id}`)}>
          <Cell>
            <span className="text-ink cursor-pointer hover:text-profit transition-colors">
              {row.name}
            </span>
          </Cell>
          <Cell>{row.clientName}</Cell>
          <Cell muted>{row.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
          <Cell align="right" mono>
            {row.weeks}
          </Cell>
          <Cell align="right" mono>
            {hours(row.hours)}
          </Cell>
          {showsRates && (
            <>
              <Cell align="right" mono>
                {rate(row.payRate)}
              </Cell>
              <Cell align="right" mono muted>
                {row.loadFactor === null ? '—' : Number(row.loadFactor).toFixed(3)}
              </Cell>
            </>
          )}
          <Cell align="right" mono>
            {rate(row.billRate)}
          </Cell>
          <Cell align="right" mono>
            {rate(row.grossProfitPerHour)}
          </Cell>
          <Cell align="right" mono>
            <span className="text-profit">{money(row.grossProfit)}</span>
          </Cell>
        </Row>
      ))}
    </Table>
  );
}

function ClientTable({
  rows,
  navigate,
}: {
  rows: readonly ClientRow[];
  navigate: (hash: string) => void;
}) {
  const accessor = useCallback((row: ClientRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      contractors: row.contractors,
      hours: row.hours,
      billed: row.billed,
      grossProfit: row.grossProfit,
      margin: marginPercent(row.grossProfit, row.billed),
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(rows, accessor);

  return (
    <Table
      head={[
        { label: 'Customer', sortKey: 'name' },
        { label: 'Contractors', align: 'right', sortKey: 'contractors' },
        { label: 'Hours', align: 'right', sortKey: 'hours' },
        { label: 'Billed', align: 'right', sortKey: 'billed' },
        { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
        { label: 'Margin', align: 'right', sortKey: 'margin' },
      ]}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={onSort}
    >
      {sorted.map((row) => (
        <Row key={row.id} onClick={() => navigate(`client/${row.id}`)}>
          <Cell>
            <span className="text-ink cursor-pointer hover:text-profit transition-colors">
              {row.name}
            </span>
          </Cell>
          <Cell align="right" mono>
            {row.contractors}
          </Cell>
          <Cell align="right" mono>
            {hours(row.hours)}
          </Cell>
          <Cell align="right" mono>
            {money(row.billed)}
          </Cell>
          <Cell align="right" mono>
            <span className="text-profit">{money(row.grossProfit)}</span>
          </Cell>
          <Cell align="right" mono muted>
            {marginPercent(row.grossProfit, row.billed)}
          </Cell>
        </Row>
      ))}
    </Table>
  );
}

function RecruiterTable({
  rows,
  navigate,
}: {
  rows: readonly RecruiterRow[];
  navigate: (hash: string) => void;
}) {
  const accessor = useCallback((row: RecruiterRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      contractors: row.contractors,
      hours: row.hours,
      grossProfit: row.grossProfit,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(rows, accessor);

  return (
    <Table
      head={[
        { label: 'Recruiter', sortKey: 'name' },
        { label: 'Contractors', align: 'right', sortKey: 'contractors' },
        { label: 'Hours', align: 'right', sortKey: 'hours' },
        { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
      ]}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={onSort}
    >
      {sorted.map((row) => (
        <Row key={row.id} onClick={() => navigate(`recruiter/${row.id}`)}>
          <Cell>
            <span className="text-ink cursor-pointer hover:text-profit transition-colors">
              {row.name}
            </span>
          </Cell>
          <Cell align="right" mono>
            {row.contractors}
          </Cell>
          <Cell align="right" mono>
            {hours(row.hours)}
          </Cell>
          <Cell align="right" mono>
            <span className="text-profit">{money(row.grossProfit)}</span>
          </Cell>
        </Row>
      ))}
    </Table>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sync Status Bar                                                            */
/* -------------------------------------------------------------------------- */

const PHASE_LABELS: Record<string, string> = {
  fetching: 'Fetching timesheets from Oorwin',
  comparing: 'Detecting changes',
  aligning_calendar: 'Matching pay periods to Oorwin weeks',
  importing: 'Importing time entries',
  calculating_gp: 'Calculating gross profit',
  done: 'Sync complete',
};

function SyncStatusBar({ syncStatus }: { syncStatus: OorwinSyncStatusResponse | null }) {
  if (syncStatus === null) return null;

  const { currentSync, lastSync, backfill } = syncStatus;
  const loadingHistory = backfill?.status === 'running' ? backfill : null;

  // Show running sync progress
  if (currentSync !== null || loadingHistory !== null) {
    const phase = currentSync?.phase ?? 'fetching';
    const current = currentSync?.current ?? 0;
    const total = currentSync?.total ?? 0;
    const pct =
      loadingHistory !== null && loadingHistory.chunksTotal > 0
        ? Math.round((loadingHistory.chunksCompleted / loadingHistory.chunksTotal) * 100)
        : total > 0
          ? Math.round((current / total) * 100)
          : 0;

    return (
      <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 animate-spin text-teal-600" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span className="text-sm font-medium text-teal-800">
              {loadingHistory !== null
                ? `Loading timesheet history (${loadingHistory.fromDate} to ${loadingHistory.toDate})`
                : (PHASE_LABELS[phase] ?? phase)}
            </span>
          </div>
          <span className="text-xs text-teal-600">
            {loadingHistory !== null ? (
              <>
                {loadingHistory.chunksCompleted} / {loadingHistory.chunksTotal} parts ·{' '}
              </>
            ) : (
              current > 0 && (
                <>
                  {current} / {total} ·{' '}
                </>
              )
            )}
            {pct}%
          </span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-teal-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-teal-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        {(loadingHistory !== null || (currentSync !== null && currentSync.rowsFetched > 0)) && (
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-teal-600">
            {loadingHistory !== null && currentSync !== null && (
              <span>
                {PHASE_LABELS[phase] ?? phase}
                {currentSync.windowStart !== null &&
                  ` for ${currentSync.windowStart} to ${currentSync.windowEnd ?? ''}`}
              </span>
            )}
            {loadingHistory !== null && <span>{loadingHistory.rowsFetched} loaded so far</span>}
            {currentSync !== null && currentSync.rowsFetched > 0 && (
              <span>{currentSync.rowsFetched} fetched</span>
            )}
            {currentSync !== null && currentSync.rowsNew > 0 && (
              <span>{currentSync.rowsNew} new</span>
            )}
            {currentSync !== null && currentSync.rowsChanged > 0 && (
              <span>{currentSync.rowsChanged} changed</span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Show most recent sync result briefly (completed syncs)
  if (lastSync !== null && lastSync.completedAt !== null) {
    const elapsed = Date.now() - new Date(lastSync.completedAt).getTime();
    // Only show the completion banner for 30 seconds after it finishes
    if (elapsed < 30_000) {
      const isSuccess = lastSync.status === 'completed' || lastSync.status === 'no_change';
      const isFailed = lastSync.status === 'failed';

      return (
        <div
          className={`mb-3 rounded-lg border px-4 py-2 flex items-center justify-between ${
            isFailed
              ? 'border-red-200 bg-red-50'
              : isSuccess
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="flex items-center gap-2">
            {isSuccess ? (
              <svg
                className="h-4 w-4 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg
                className="h-4 w-4 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            )}
            <span
              className={`text-sm font-medium ${isFailed ? 'text-red-800' : isSuccess ? 'text-emerald-800' : 'text-amber-800'}`}
            >
              {lastSync.status === 'no_change'
                ? 'Oorwin sync complete — no changes detected'
                : lastSync.status === 'completed'
                  ? `Oorwin sync complete — ${String(lastSync.rowsNew)} new, ${String(lastSync.rowsChanged)} changed`
                  : lastSync.status === 'failed'
                    ? `Oorwin sync failed: ${lastSync.errorMessage ?? 'unknown error'}`
                    : `Oorwin sync finished with errors — ${String(lastSync.rowsNew)} new, ${String(lastSync.rowsChanged)} changed. The rest is retried on the next sync.`}
            </span>
          </div>
          <span
            className={`text-xs ${isFailed ? 'text-red-500' : isSuccess ? 'text-emerald-500' : 'text-amber-500'}`}
          >
            {[
              lastSync.periodsAffected.length > 0
                ? `${String(lastSync.periodsAffected.length)} week(s) updated`
                : null,
              lastSync.rowsAwaitingApproval > 0
                ? `${String(lastSync.rowsAwaitingApproval)} awaiting approval`
                : null,
              syncStatus.openExceptionCount > 0
                ? `${String(syncStatus.openExceptionCount)} need attention`
                : null,
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          </span>
        </div>
      );
    }
  }

  return null;
}
